import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import {
  Icon, IconButton, InfoBlock, Menu, MenuItem, MenuItemIcon, MenuItemText,
} from '@picsio/ui';
import {
  Archive, DotsVertical, Edit, OpenIn, Publish, TrashIcon,
} from '@picsio/icons';

import { getActiveProduct } from '../../../store/products/selectors';
import {
  selectEffectiveChannelState,
  selectDraftListingVariants,
  selectDraftPublishMediaReadiness,
  selectProductChannelState,
  selectSelectedListingVariants,
  selectSelectedLiveProduct,
  selectProductIsBusy,
  selectProductBusyLoadingText,
  selectEffectiveIsMasterView,
  selectSelectedListingVariantState,
} from '../../../store/selectors/productSelectors';
import { deleteProductDraft } from '../../../store/products/deleteProductDraft';
import { discardProductDraft } from '../../../store/products/discardProductDraft';
import { publishProductDraft } from '../../../store/products/publishProductDraft';
import { publishSelectedVariantDrafts } from '../../../store/products/publishSelectedVariantDrafts';
import { deleteProductLiveSnapshot } from '../../../store/products/deleteProductLiveSnapshot';
import { removeProductLiveSnapshot } from '../../../store/products/removeProductLiveSnapshot';
import { removeVariantLiveSnapshot } from '../../../store/products/removeVariantLiveSnapshot';
import { deleteVariantLiveSnapshot } from '../../../store/products/deleteVariantLiveSnapshot';
import { removeVariantFromChannel } from '../../../store/products/removeVariantFromChannel';
import { revertVariantDraftToPublished } from '../../../store/products/revertVariantDraftToPublished';
import { createProductDraftFromLive } from '../../../store/products/createProductDraftFromLive';
import { createVariantDraftFromLive } from '../../../store/products/createVariantDraftFromLive';
import { createProductDraftFromMaster } from '../../../store/products/createProductDraftFromMaster';
import { archiveListing } from '../../../store/products/archiveListing';
import localization from '../../../shared/strings';
import {
  findSalesChannelByContextId,
  hasConnectedShopifyStore,
  isContextChannelDisconnected,
} from '../../teammates/views/Channels/helpers/salesChannelState';
import { showDialog } from '../../dialog';
import Tooltip from '../../Tooltip/Tooltip';
import { productStateTabConstants } from '../constants/productStateTabConstants';
import { showVariantsSelectionDialog } from '../VariantsSelectionDialog';
import ButtonLoader from '../ConnectChannelSection/ButtonLoader';
import {
  buildShopifyAdminProductUrl,
  buildShopifyAdminVariantUrl,
  getShopifyAdminStoreSlug,
} from '../helpers/buildShopifyAdminUrl';
import runPublishUnmappedFieldsGuard from '../helpers/runPublishUnmappedFieldsGuard';
import ensureDraftPublishMediaLoaded from './helpers/ensureDraftPublishMediaLoaded';
import getPublishDisabledMediaTooltip from './helpers/getPublishDisabledMediaTooltip';
import styles from './styles.module.scss';

const createInfoNote = (shouldShow, text) => {
  if (!shouldShow) return null;

  return (
    <InfoBlock className="m-t-16">
      {text}
    </InfoBlock>
  );
};

const ProductStateTabsBar = ({ productId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { variantId } = useMemo(() => getActiveProduct(location), [location]);
  const menuRef = useRef();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const {
    activeTab,
    contextChannelId,
    contextChannelOption,
    hasStaging,
    hasLive,
  } = useSelector((state) => selectEffectiveChannelState(state, productId, variantId));
  const productChannelState = useSelector((state) => selectProductChannelState(state, productId));
  const isMasterView = useSelector((state) => selectEffectiveIsMasterView(state, productId, variantId));
  const product = useSelector((state) => state.products.items?.find(({ _id }) => productId === _id) || {});
  const listingVariants = useSelector((state) => selectSelectedListingVariants(state, productId));
  const draftListingVariants = useSelector((state) => selectDraftListingVariants(state, productId));
  const {
    listingVariant: selectedListingVariant,
    hasVariantLive,
    isLastDraftVariant,
    isLastLiveVariant,
  } = useSelector((state) => selectSelectedListingVariantState(state, productId, variantId));
  const isShopifyConnected = useSelector((state) => hasConnectedShopifyStore(state.user.team));
  const team = useSelector((state) => state.user.team);
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const busyLoadingText = useSelector((state) => selectProductBusyLoadingText(state, productId));
  const publishMediaReadiness = useSelector((state) => (
    selectDraftPublishMediaReadiness(state, productId)
  ));
  const isChannelDisconnected = isContextChannelDisconnected(team, contextChannelId);
  const isActionsDisabled = isBusy || isChannelDisconnected;
  const isPublishDisabled = isActionsDisabled || !publishMediaReadiness.canPublish;
  const connectionLostTooltip = isChannelDisconnected
    ? localization.PRODUCT_INFO.publishedLayerConnectionLostTooltip
    : null;
  const publishDisabledMediaTooltip = getPublishDisabledMediaTooltip(publishMediaReadiness);
  const publishActionTooltip = connectionLostTooltip || (
    activeTab === productStateTabConstants.DRAFT ? publishDisabledMediaTooltip : null
  );
  const isVariantView = Boolean(variantId);

  const publishMediaReadinessRef = useRef(publishMediaReadiness);
  publishMediaReadinessRef.current = publishMediaReadiness;

  useEffect(() => {
    if (activeTab !== productStateTabConstants.DRAFT || !hasStaging) return undefined;

    ensureDraftPublishMediaLoaded({
      dispatch,
      productId,
      readiness: publishMediaReadinessRef.current,
    });

    const onWindowFocus = () => {
      ensureDraftPublishMediaLoaded({
        dispatch,
        productId,
        readiness: publishMediaReadinessRef.current,
      });
    };

    window.addEventListener('focus', onWindowFocus);
    return () => window.removeEventListener('focus', onWindowFocus);
  }, [
    activeTab,
    dispatch,
    hasStaging,
    productId,
    publishMediaReadiness.needsFetch,
    publishMediaReadiness.reason,
    publishMediaReadiness.listingProductId,
    publishMediaReadiness.channelId,
  ]);
  const hasListingVariants = listingVariants.length > 0;
  const liveProduct = useSelector((state) => selectSelectedLiveProduct(state, productId));
  const isLiveArchived = liveProduct?.status === 'ARCHIVED';

  const shopifyAdminStoreSlug = useMemo(() => {
    const channel = findSalesChannelByContextId(team, contextChannelId);
    return getShopifyAdminStoreSlug(channel?.store?.myshopify_domain);
  }, [team, contextChannelId]);

  const shopifyAdminProductUrl = useMemo(() => buildShopifyAdminProductUrl({
    storeSlug: shopifyAdminStoreSlug,
    productStoreId: liveProduct?.storeId,
  }), [shopifyAdminStoreSlug, liveProduct?.storeId]);

  const shopifyAdminVariantUrl = useMemo(() => buildShopifyAdminVariantUrl({
    storeSlug: shopifyAdminStoreSlug,
    productStoreId: liveProduct?.storeId,
    variantStoreId: selectedListingVariant?.live?.storeId,
  }), [shopifyAdminStoreSlug, liveProduct?.storeId, selectedListingVariant?.live?.storeId]);

  const onOpenInShopifyAdmin = useCallback((url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const tabsList = useMemo(() => ([
    {
      id: productStateTabConstants.DRAFT,
      text: 'Draft',
    },
    {
      id: productStateTabConstants.PUBLISHED,
      text: 'Published',
    },
  ]), []);

  const toggleMenu = useCallback(() => setMenuOpen((prev) => !prev), []);

  const runStagingPublishWithGuard = useCallback(async (publishFn) => {
    await runPublishUnmappedFieldsGuard({
      channelIds: [contextChannelId],
      dispatch,
      salesChannels: team?.salesChannels || [],
      onProceed: publishFn,
      onAbort: () => {},
    });
  }, [contextChannelId, dispatch, team?.salesChannels]);

  const onStageTrashButtonClick = useCallback(() => {
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER;
    const channelName = contextChannelOption?.label || contextChannelId || 'channel';
    const lastDraftVariantNote = createInfoNote(
      isVariantView && isLastDraftVariant,
      localization.DIALOGS.PRODUCT_DIALOGS.REMOVE_VARIANT_FROM_CHANNEL.LAST_DRAFT_VARIANT_NOTE,
    );

    if (isVariantView && selectedListingVariant?._id) {
      const variantStrings = localization.DIALOGS.PRODUCT_DIALOGS.REMOVE_VARIANT_FROM_CHANNEL;

      if (hasVariantLive) {
        showDialog({
          title: variantStrings.TITLE(channelName),
          text: variantStrings.TEXT(channelName),
          textBtnCancel: variantStrings.BTN_CANCEL,
          textBtnOk: variantStrings.BTN_OK,
          buttonOkColor: 'danger',
          children: lastDraftVariantNote,
          onOk: () => dispatch(revertVariantDraftToPublished({
            masterProductId: productId,
            channelId: contextChannelId,
            listingVariantId: selectedListingVariant._id,
          })),
        });
        return;
      }

      showDialog({
        title: variantStrings.TITLE(channelName),
        text: variantStrings.TEXT(channelName),
        textBtnCancel: variantStrings.BTN_CANCEL,
        textBtnOk: variantStrings.BTN_OK,
        buttonOkColor: 'danger',
        children: lastDraftVariantNote,
        onOk: () => dispatch(removeVariantFromChannel({
          masterProductId: productId,
          channelId: contextChannelId,
          channelName,
          listingVariantId: selectedListingVariant._id,
        })),
      });
      return;
    }

    if (hasLive) {
      showDialog({
        title: strings.DISCARD_DRAFT.TITLE,
        text: hasListingVariants ? strings.DISCARD_DRAFT.TEXT_VARIANTS : strings.DISCARD_DRAFT.TEXT,
        textBtnCancel: strings.DISCARD_DRAFT.BTN_CANCEL,
        textBtnOk: hasListingVariants ? strings.DISCARD_DRAFT.BTN_OK_VARIANTS : strings.DISCARD_DRAFT.BTN_OK,
        buttonOkColor: 'danger',
        children: lastDraftVariantNote,
        onOk: () => dispatch(discardProductDraft({ productId, channelId: contextChannelId })),
      });
      return;
    }

    showDialog({
      title: strings.REMOVE_DRAFT.TITLE(channelName),
      text: hasListingVariants ? strings.REMOVE_DRAFT.TEXT_VARIANTS(channelName) : strings.REMOVE_DRAFT.TEXT(channelName),
      textBtnCancel: strings.REMOVE_DRAFT.BTN_CANCEL,
      textBtnOk: hasListingVariants ? strings.REMOVE_DRAFT.BTN_OK_VARIANTS : strings.REMOVE_DRAFT.BTN_OK,
      buttonOkColor: 'danger',
      children: lastDraftVariantNote,
      onOk: () => dispatch(deleteProductDraft({ masterProductId: productId, channelId: contextChannelId })),
    });
  }, [
    contextChannelId,
    contextChannelOption?.label,
    dispatch,
    hasLive,
    hasListingVariants,
    hasVariantLive,
    isLastDraftVariant,
    isVariantView,
    productId,
    selectedListingVariant?._id,
  ]);

  const onStagePublishButtonClick = useCallback(() => {
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER;
    const channelName = contextChannelOption?.label || contextChannelId || 'channel';
    const onPublish = (listingVariantIds) => dispatch(publishProductDraft({
      productId,
      channelId: contextChannelId,
      ...(listingVariantIds?.length ? { listingVariantIds } : {}),
    }));
    const onPublishVariants = (listingVariantIds) => dispatch(publishSelectedVariantDrafts({
      productId,
      channelId: contextChannelId,
      listingVariantIds,
    }));
    const publishCurrentVariant = () => onPublishVariants([selectedListingVariant._id]);
    const shouldShowVariantPublishNote = isVariantView && productChannelState.hasStaging && !productChannelState.hasLive;

    if (!isVariantView && hasLive && draftListingVariants.length) {
      showVariantsSelectionDialog({
        productTitle: product.title || '',
        variants: draftListingVariants,
        strings: localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL.UPDATE_EXISTING,
        variantsOptional: true,
        returnAllSelectedAsUndefined: false,
        onConfirm: ({ variantIds, isProductSelected }) => {
          if (isProductSelected) {
            // Product (+ optional variants): promote listing draft; selected variants set publish scope.
            runStagingPublishWithGuard(() => onPublish(variantIds));
            return;
          }

          runStagingPublishWithGuard(() => onPublishVariants(variantIds));
        },
      });
      return;
    }

    if (!isVariantView && !hasLive && draftListingVariants.length) {
      showVariantsSelectionDialog({
        productTitle: product.title || '',
        variants: draftListingVariants,
        strings: strings.PUBLISH_VARIANTS,
        returnAllSelectedAsUndefined: false,
        onConfirm: ({ variantIds }) => {
          // First publish must promote listing product draft (promoteProduct),
          // same as hasLive + isProductSelected path above.
          runStagingPublishWithGuard(() => onPublish(variantIds));
        },
      });
      return;
    }

    // No draft listing variants: publish the product draft as-is (publishListing), do not
    // prompt to create/publish master variants via publishDirectly.

    if (shouldShowVariantPublishNote) {
      const publishVariantStrings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER.PUBLISH_VARIANT_TO_CHANNEL;
      showDialog({
        title: publishVariantStrings.TITLE,
        text: publishVariantStrings.TEXT(channelName),
        textBtnCancel: localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL.BTN_CANCEL,
        textBtnOk: localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.PUBLISH_TO_CHANNEL,
        children: createInfoNote(true, localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL.VARIANT_NOTE),
        onOk: () => {
          runStagingPublishWithGuard(publishCurrentVariant);
        },
      });
      return;
    }

    if (hasLive) {
      showDialog({
        title: strings.PUBLISH_CHANGES_LIVE_ACTIVE.TITLE(channelName),
        text: strings.PUBLISH_CHANGES_LIVE_ACTIVE.TEXT(channelName),
        textBtnCancel: strings.PUBLISH_CHANGES_LIVE_ACTIVE.BTN_CANCEL,
        textBtnOk: strings.PUBLISH_CHANGES_LIVE_ACTIVE.BTN_OK,
        onOk: () => {
          runStagingPublishWithGuard(
            isVariantView ? publishCurrentVariant : () => onPublish(),
          );
        },
      });
      return;
    }

    if (isVariantView) {
      runStagingPublishWithGuard(publishCurrentVariant);
      return;
    }

    runStagingPublishWithGuard(() => onPublish());
  }, [contextChannelId, contextChannelOption?.label, draftListingVariants, dispatch, hasLive, isVariantView, product?.title, productChannelState.hasLive, productChannelState.hasStaging, productId, runStagingPublishWithGuard, selectedListingVariant?._id]);

  const onLiveTrashButtonClick = useCallback(() => {
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.LIVE_LAYER.REMOVE_PRODUCT;
    const channelLabel = contextChannelOption?.label;

    const title = hasListingVariants
      ? strings.TITLE_VARIANTS(channelLabel)
      : strings.TITLE(channelLabel);

    let text;
    if (hasListingVariants && hasStaging) {
      text = strings.TEXT_VARIANTS_WITH_STAGING(channelLabel);
    } else if (hasListingVariants && !hasStaging) {
      text = strings.TEXT_VARIANTS(channelLabel);
    } else if (!hasListingVariants && hasStaging) {
      text = strings.TEXT_WITH_STAGING(channelLabel);
    } else {
      text = strings.TEXT(channelLabel);
    }

    const onOk = hasStaging
      ? () => dispatch(removeProductLiveSnapshot({ productId, channelId: contextChannelId }))
      : () => dispatch(deleteProductLiveSnapshot({ productId, channelId: contextChannelId }));

    showDialog({
      title,
      text,
      textBtnCancel: strings.BTN_CANCEL,
      textBtnOk: strings.BTN_OK,
      buttonOkColor: 'danger',
      onOk,
    });
  }, [contextChannelId, contextChannelOption?.label, dispatch, hasListingVariants, hasStaging, productId]);

  const onVariantUnpublishButtonClick = useCallback(() => {
    if (!selectedListingVariant?._id) return;
    const channelName = contextChannelOption?.label || contextChannelId || 'channel';

    const strings = localization.DIALOGS.PRODUCT_DIALOGS.UNPUBLISH_VARIANT_FROM_CHANNEL;

    let onOk;
    if (isLastLiveVariant) {
      onOk = hasStaging
        ? () => dispatch(removeProductLiveSnapshot({ productId, channelId: contextChannelId }))
        : () => dispatch(deleteProductLiveSnapshot({ productId, channelId: contextChannelId }));
    } else {
      onOk = hasStaging
        ? () => dispatch(removeVariantLiveSnapshot({
          masterProductId: productId,
          channelId: contextChannelId,
          variantId: selectedListingVariant._id,
        }))
        : () => dispatch(deleteVariantLiveSnapshot({
          masterProductId: productId,
          channelId: contextChannelId,
          variantId: selectedListingVariant._id,
        }));
    }

    showDialog({
      title: strings.TITLE(channelName),
      text: strings.TEXT(channelName),
      // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
      children: isLastLiveVariant ? <InfoBlock className="m-t-16">{strings.VARIANT_NOTE}</InfoBlock> : null,
      textBtnCancel: strings.BTN_CANCEL,
      textBtnOk: strings.BTN_OK,
      buttonOkColor: 'danger',
      onOk,
    });
  }, [
    contextChannelId,
    contextChannelOption?.label,
    dispatch,
    hasStaging,
    isLastLiveVariant,
    productId,
    selectedListingVariant?._id,
  ]);

  const onCreateDraftFromLive = useCallback(() => {
    if (isVariantView) {
      dispatch(createVariantDraftFromLive({ productId, channelId: contextChannelId, variantId }));
    } else {
      dispatch(createProductDraftFromLive({ productId, channelId: contextChannelId }));
    }
  }, [contextChannelId, dispatch, isVariantView, productId, variantId]);

  const onPublishDraftFromLive = useCallback(() => {
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER.CREATE_DRAFT_FROM_PUBLISHED;
    showDialog({
      title: strings.TITLE,
      text: strings.TEXT(contextChannelOption?.label || ''),
      textBtnCancel: strings.BTN_CANCEL,
      textBtnOk: strings.BTN_OK,
      onOk: onCreateDraftFromLive,
    });
  }, [contextChannelOption?.label, onCreateDraftFromLive]);

  const onLiveEditButtonClick = useCallback(() => {
    if (hasStaging) {
      onPublishDraftFromLive();
      return;
    }

    onCreateDraftFromLive();
  }, [hasStaging, onCreateDraftFromLive, onPublishDraftFromLive]);

  const onCreateDraftFromMaster = useCallback(() => {
    const strings = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.STAGE_LAYER.CREATE_DRAFT_FROM_MASTER;
    showDialog({
      title: strings.TITLE,
      text: strings.TEXT(contextChannelOption?.label || ''),
      textBtnCancel: strings.BTN_CANCEL,
      textBtnOk: strings.BTN_OK,
      onOk: () => {
        dispatch(createProductDraftFromMaster({
          productId,
          channelIds: [contextChannelId],
          copyMasterAssets: true,
          overrideExisting: true,
          ...(variantId ? { masterVariantIds: [variantId] } : { productOnly: true }),
        }));
      },
    });
  }, [contextChannelId, contextChannelOption, dispatch, productId, variantId]);

  const onArchiveListingClick = useCallback(() => {
    dispatch(archiveListing({ masterProductId: productId, channelId: contextChannelId }));
  }, [dispatch, productId, contextChannelId]);

  const menuItems = useMemo(() => {
    if (hasStaging && activeTab === productStateTabConstants.DRAFT) {
      return [
        {
          id: `discard-${productStateTabConstants.DRAFT}`,
          text: 'Delete draft',
          icon: TrashIcon,
          onClick: onStageTrashButtonClick,
        },
        {
          id: 'create-draft-from-master',
          text: 'Create draft from Master',
          icon: Edit,
          onClick: onCreateDraftFromMaster,
        },
        ...(hasLive ? [{
          id: 'create-draft-from-published',
          text: 'Create draft from Published',
          icon: Edit,
          onClick: onPublishDraftFromLive,
        }] : []),
      ];
    }

    if (hasLive && activeTab === productStateTabConstants.PUBLISHED) {
      if (isVariantView && hasVariantLive) {
        return [
          {
            id: `unpublish-variant-${productStateTabConstants.PUBLISHED}`,
            text: `Delete from ${contextChannelOption?.label || 'channel'}`,
            icon: TrashIcon,
            onClick: onVariantUnpublishButtonClick,
          },
          ...(shopifyAdminVariantUrl ? [{
            id: 'open-in-shopify-admin',
            text: localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.OPEN_IN_SHOPIFY_ADMIN,
            icon: OpenIn,
            onClick: () => onOpenInShopifyAdmin(shopifyAdminVariantUrl),
          }] : []),
        ];
      }

      if (!isVariantView) {
        return [
          {
            id: `unpublish-product-${productStateTabConstants.PUBLISHED}`,
            text: `Delete from ${contextChannelOption?.label || 'channel'}`,
            icon: TrashIcon,
            onClick: onLiveTrashButtonClick,
          },
          {
            id: 'archive-product',
            text: `Archive in ${contextChannelOption?.label || 'channel'}`,
            icon: Archive,
            onClick: onArchiveListingClick,
            disabled: isLiveArchived,
          },
          ...(shopifyAdminProductUrl ? [{
            id: 'open-in-shopify-admin',
            text: localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.OPEN_IN_SHOPIFY_ADMIN,
            icon: OpenIn,
            onClick: () => onOpenInShopifyAdmin(shopifyAdminProductUrl),
          }] : []),
        ];
      }

      return [];
    }

    return [];
  }, [
    hasStaging,
    activeTab,
    hasLive,
    isVariantView,
    hasVariantLive,
    onStageTrashButtonClick,
    onCreateDraftFromMaster,
    onPublishDraftFromLive,
    contextChannelOption?.label,
    onLiveTrashButtonClick,
    onVariantUnpublishButtonClick,
    onArchiveListingClick,
    isLiveArchived,
    shopifyAdminProductUrl,
    shopifyAdminVariantUrl,
    onOpenInShopifyAdmin,
  ]);

  if (isMasterView || (!hasStaging && !hasLive)) return null;

  return (
    <div className={styles.actionsRow}>
      <If condition={(hasStaging || hasLive)}>
        {tabsList.map((tab) => {
          const isLiveActive = (tab.id === productStateTabConstants.PUBLISHED && hasLive);
          const isStagingActive = (tab.id === productStateTabConstants.DRAFT && hasStaging);
          if (!isLiveActive && !isStagingActive) return null;
          if (hasStaging && hasLive && tab.id !== activeTab) return null;
          return (
            <div key={tab.id} className={cn(styles.stateLabel, styles.existILife)}>
              <span>{tab.text}</span>
              <span className={cn(styles.dot, styles[`${tab.id}Dot`])} />
              <If condition={contextChannelOption?.label}>
                <span className={styles.channelLabel}>
                  ({' '}
                  <span className={styles.channelLabelText}>{contextChannelOption.label}</span>
                  {' '})
                </span>
              </If>
            </div>
          );
        })}
      </If>
      <If condition={isShopifyConnected}>
        <Tooltip content={publishActionTooltip}>
          <span className={styles.actionsMainWrapper}>
            <div className={styles.actionsMain}>
              <If condition={activeTab === productStateTabConstants.DRAFT}>
                <button
                  type="button"
                  className={cn(styles.primaryAction, styles.publishAction)}
                  disabled={isPublishDisabled}
                  onClick={onStagePublishButtonClick}
                >
                  <Choose>
                    <When condition={isBusy}>
                      <ButtonLoader />
                    </When>
                    <Otherwise>
                      <Icon size="lg"><Publish /></Icon>
                    </Otherwise>
                  </Choose>
                  <span>{isBusy ? busyLoadingText : localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.PUBLISH_TO_CHANNEL}</span>
                </button>
              </If>
              <If condition={activeTab === productStateTabConstants.PUBLISHED}>
                <Tooltip
                  content={localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.EDIT_ATTRIBUTES_TOOLTIP}
                  placement="top"
                >
                  <span>
                    <button
                      type="button"
                      className={styles.primaryAction}
                      disabled={isActionsDisabled}
                      onClick={onLiveEditButtonClick}
                    >
                      <Choose>
                        <When condition={isBusy}>
                          <ButtonLoader />
                        </When>
                        <Otherwise>
                          <Icon size="lg"><Edit /></Icon>
                        </Otherwise>
                      </Choose>
                      <span>{isBusy ? busyLoadingText : localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS.ACTIONS.EDIT_ATTRIBUTES}</span>
                    </button>
                  </span>
                </Tooltip>
              </If>
              <div className={styles.tabsIcons}>
                <If condition={menuItems.length > 0}>
                  <IconButton
                    ref={menuRef}
                    size="lg"
                    disabled={isActionsDisabled}
                    className={cn({ [styles.menuButtonActive]: isMenuOpen })}
                    onClick={toggleMenu}
                  >
                    <DotsVertical />
                  </IconButton>
                  <Menu
                    target={menuRef}
                    arrow
                    placement="bottom-end"
                    isOpen={isMenuOpen}
                    outsideClickListener
                    disablePortal
                    onClose={toggleMenu}
                  >
                    {menuItems.map((item) => {
                      const ControlIcon = item.icon;

                      return (
                        <MenuItem
                          key={item.id}
                          className="menuItemDefault"
                          disabled={item?.disabled}
                          onClick={() => {
                            item.onClick();
                            toggleMenu();
                          }}
                        >
                          <MenuItemIcon size="md">
                            <ControlIcon />
                          </MenuItemIcon>
                          <MenuItemText primary={item.text} />
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </If>
              </div>
            </div>
          </span>
        </Tooltip>
      </If>
    </div>
  );
};

ProductStateTabsBar.propTypes = {
  productId: PropTypes.string.isRequired,
};

export default ProductStateTabsBar;
